import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { truncateLength } from "@/components/app-layout/consts";
import { UserDisplayName } from "@/components/display/UserDisplayName";
import { GroupedTable } from "@/components/GroupedTable/GroupedTable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Row } from "@/components/ui/flex";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CreateUserButton } from "@/components/user/CreateUserButton";
import { UserRoleSelection } from "@/components/user/RoleSelection";
import { UserBulkActions } from "@/components/user/UserBulkActions";
import { useAuthDetails } from "@/context/auth-context";
import { useGlobalUserData, useGlobalUsersData } from "@/data/use-users-data";
import { UserRole } from "@/lib/consts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

export default function GlobalAdminUsersPage() {
  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Global User Management</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <Row className="items-center justify-between">
          <h1 className="text-2xl font-bold">Users</h1>
          <CreateUserButton />
        </Row>
        <Contents />
      </PageBody>
    </Page>
  );
}

function Contents() {
  const { users } = useGlobalUsersData();
  const authDetails = useAuthDetails();
  const { deleteUser, updateUser } = useGlobalUserData(undefined);
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<UserRole | undefined>(undefined);

  const shouldRenderCheckedActions = authDetails?.role === "super";

  const handleUserUpdate = (role: UserRole, userUuid: string) => {
    updateUser(
      { body: { role }, userUuid },
      {
        onSuccess: (data) => {
          if (data.response.ok) {
            toast.success("User role updated successfully");
          } else {
            toast.error("Failed to update user role");
          }
        },
        onError: () => {
          toast.error("Failed to update user role");
        },
      },
    );
  };

  const checkUserIsCurrentUser = (role: UserRole, userUuid: string) => {
    setRole(role);
    return authDetails?.uuid === userUuid;
  };

  return (
    <>
      <GroupedTable
        isSearchable
        isSortable
        data={[
          {
            groupId: "users",
            label: "Users",
            items: users,
          },
        ]}
        columns={[
          {
            key: "name",
            label: "Users",
          },
          {
            key: "email",
            label: "Email",
          },
          {
            key: "role",
            label: "Role",
          },
        ]}
        renderCell={{
          name: (_, row) => {
            return <UserDisplayName user={row} />;
          },
          email: (_, row) =>
            row.email && row.email.length > truncateLength ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>{`${row.email.slice(0, truncateLength)}…`}</span>
                  </TooltipTrigger>
                  <TooltipContent>{row.email}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              row.email
            ),
          role: (value, row) => (
            <UserRoleSelection
              value={value}
              disableRoleOption={[value]}
              onChange={(role) =>
                checkUserIsCurrentUser(role, row.uuid)
                  ? setOpen(true)
                  : handleUserUpdate(role, row.uuid)
              }
            />
          ),
        }}
        renderActions={({ row }) => (
          <Link to="/admin/users/$userUuid" params={{ userUuid: row.uuid }}>
            <FontAwesomeIcon icon={faEye} />
          </Link>
        )}
        checkedActions={(selectedItems) =>
          shouldRenderCheckedActions ? (
            <UserBulkActions
              selectedItems={selectedItems}
              deleteUser={deleteUser}
              variant="global"
            />
          ) : undefined
        }
      />
      <AlertDialog open={open} onOpenChange={() => setOpen((prev) => !prev)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Are you sure you want to change your own role?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This may affect your permissions and you won&apos;t be able to
              perform certain actions. Please proceed with caution.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleUserUpdate(role!, authDetails!.uuid)}
              asChild
            >
              <Button size="sm">Change my role</Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
